/* eslint-disable @typescript-eslint/no-explicit-any */
import { SectionFields } from '../../../../types';
import { VALUE_MAP } from './constants';

/** Convert snake_case to camelCase. */
export const snakeToCamel = (str: string): string => {
  return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
}

/** Convert camelCase to snake_case. */
export const camelToSnake = (str: string): string => {
  return str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
}

/** Rename a record's keys from camelCase to snake_case. Values are unchanged. */
export const toSnakeCaseKeys = (
  record: Record<string, unknown>
): Record<string, unknown> =>
  Object.entries(record).reduce<Record<string, unknown>>(
    (result, [key, value]) => {
      result[camelToSnake(key)] = value;
      return result;
    },
    {}
  );

/** Rename a record's keys from snake_case to camelCase. Values are unchanged. */
export const toCamelCaseKeys = (
  record: Record<string, unknown>
): Record<string, unknown> =>
  Object.entries(record).reduce<Record<string, unknown>>(
    (result, [key, value]) => {
      result[snakeToCamel(key)] = value;
      return result;
    },
    {}
  );

/**
 * Validate and extract a single value for API mapping.
 * Returns string from { value } or first element's .value; otherwise undefined.
 */
export const getValidatedFieldValue = (fieldValue: unknown): string | undefined => {
  if (fieldValue === null || fieldValue === undefined || fieldValue === '') {
    return undefined;
  }
  if (typeof fieldValue === 'string') {
    return undefined;
  }
  if (Array.isArray(fieldValue)) {
    if (fieldValue.length === 0) return undefined;
    const first = fieldValue[0];
    if (
      first &&
      typeof first === 'object' &&
      'value' in first
    ) {
      return first.value as string;
    }
    return undefined;
  }
  if (
    typeof fieldValue === 'object' &&
    fieldValue !== null &&
    'value' in fieldValue
  ) {
    return fieldValue.value as string;
  }
  return undefined;
}

/** True unless value is undefined, null, or an empty string. */
export const isPresent = (value: unknown): boolean =>
  value !== undefined && value !== null && value !== '';

/**
 * Stricter presence check: also rejects whitespace-only strings and
 * empty arrays/objects. Used for optional "rejection details" style fields.
 */
export const isMeaningful = (value: unknown): boolean => {
  if (!isPresent(value)) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (typeof value === 'object') {
    return Array.isArray(value)
      ? value.length > 0
      : Object.keys(value).length > 0;
  }
  return true;
};

/**
 * Build a fields object from snake_case entries, keeping only values that
 * pass the predicate (defaults to isPresent). Lets sections declare optional
 * fields explicitly while preserving "skip empty" behaviour.
 */
export const pickFields = (
  entries: Record<string, unknown>,
  predicate: (value: unknown) => boolean = isPresent
): Record<string, any> => {
  const result: Record<string, any> = {};
  Object.entries(entries).forEach(([key, value]) => {
    if (predicate(value)) {
      result[key] = value;
    }
  });
  return result;
};

/**
 * Transform a single API field value for form state (arrays for lawyer/partner,
 * VALUE_MAP lookups, verify → 'verified', etc.).
 */
export const transformFieldValue = (fieldName: string, value: any): any => {
  if (value === null || value === undefined) {
    return '';
  }
  if (
    fieldName.match(/lawyer|partner/i) &&
    !fieldName.match(
      /auth_approve|approve_comments|reject_details|verify_details|verify_bank|verify_currency|verify_reason|comments/i
    )
  ) {
    return Array.isArray(value) ? value : value ? [value] : [];
  }
  if (fieldName.match(/verify/)) {
    return value ? 'verified' : '';
  }
  if (VALUE_MAP[fieldName]?.[value]) {
    return VALUE_MAP[fieldName][value];
  }
  return value;
}

/**
 * Map a section's fields through transformFieldValue (snake_case keys unchanged).
 */
export const mapSection = <T extends SectionFields>(fields: T): Record<string, any> => {
  const mapped: Record<string, any> = {};
  Object.entries(fields).forEach(([fieldName, value]) => {
    mapped[fieldName] = transformFieldValue(fieldName, value);
  });
  return mapped;
}
